import React, { useEffect, useRef, useState } from "react";
import { FiBookOpen, FiEye, FiMinus, FiMoon, FiPlus, FiRotateCcw, FiSun, FiType } from "react-icons/fi";

import { useAccessibility } from "../context/AccessibilityContext";
import styles from "./AccessibilityMenu.module.css";

/**
 * Panel „Dostępność" — przycisk-oko z listą ustawień, przeniesiony z nagłówka mPracownika.
 * Jedna instancja obsługuje każdy kontekst (nagłówek aplikacji, ekran logowania, portal
 * gościa), bo cały stan żyje w AccessibilityContext, a komponent tylko go pokazuje.
 *
 * Opcje: rozmiar tekstu (5 stopni), WYSOKI kontrast, ZMNIEJSZONY kontrast (dodatek
 * względem mPracownika — dla osób nadwrażliwych na światło), tryb czytania, reset.
 */
const AccessibilityMenu = ({ align = "right" }) => {
    const {
        fontScaleLabel,
        highContrast,
        lowContrast,
        readerMode,
        canIncrease,
        canDecrease,
        isDefault,
        increaseFontScale,
        decreaseFontScale,
        toggleHighContrast,
        toggleLowContrast,
        toggleReaderMode,
        resetAll,
    } = useAccessibility();

    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onClick = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const onKey = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const toggleRow = (label, icon, active, onToggle) => (
        <button
            type="button"
            className={styles.row}
            onClick={onToggle}
            role="switch"
            aria-checked={active}
        >
            <span className={styles.rowIcon} aria-hidden="true">{icon}</span>
            <span className={styles.rowLabel}>{label}</span>
            <span className={`${styles.toggle} ${active ? styles.toggleOn : ""}`}>
                {active ? "WŁ." : "WYŁ."}
            </span>
        </button>
    );

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={`${styles.trigger} ${!isDefault ? styles.triggerActive : ""}`}
                onClick={() => setOpen((value) => !value)}
                title="Dostępność"
                aria-label="Ustawienia dostępności"
                aria-expanded={open}
            >
                <FiEye size={16} />
            </button>

            {open && (
                <div
                    className={`${styles.panel} ${align === "left" ? styles.panelLeft : ""}`}
                    role="menu"
                    aria-label="Ustawienia dostępności"
                >
                    <div className={styles.head}>Dostępność</div>

                    {/* Rozmiar tekstu */}
                    <div className={styles.row}>
                        <span className={styles.rowIcon} aria-hidden="true"><FiType size={15} /></span>
                        <span className={styles.rowLabel}>Rozmiar tekstu</span>
                        <span className={styles.scaleControls}>
                            <button
                                type="button"
                                className={styles.scaleBtn}
                                onClick={decreaseFontScale}
                                disabled={!canDecrease}
                                title="Zmniejsz czcionkę"
                                aria-label="Zmniejsz czcionkę"
                            >
                                <FiMinus size={13} />
                            </button>
                            <span className={styles.scaleValue}>{fontScaleLabel}</span>
                            <button
                                type="button"
                                className={styles.scaleBtn}
                                onClick={increaseFontScale}
                                disabled={!canIncrease}
                                title="Powiększ czcionkę"
                                aria-label="Powiększ czcionkę"
                            >
                                <FiPlus size={13} />
                            </button>
                        </span>
                    </div>

                    {toggleRow("Wysoki kontrast", <FiSun size={15} />, highContrast, toggleHighContrast)}
                    {toggleRow("Zmniejszony kontrast", <FiMoon size={15} />, lowContrast, toggleLowContrast)}
                    {toggleRow("Tryb czytania", <FiBookOpen size={15} />, readerMode, toggleReaderMode)}

                    <button
                        type="button"
                        className={styles.resetBtn}
                        onClick={resetAll}
                        disabled={isDefault}
                    >
                        <FiRotateCcw size={13} /> Przywróć domyślne
                    </button>
                </div>
            )}
        </div>
    );
};

export default AccessibilityMenu;
